import classNames from 'classnames';
import React from 'react';

import DropdownIcon, {
	DropdownIconStyle,
} from '~/components/patterns/icons/DropdownIcon';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useNavigationLinkClick from '~/hooks/useNavigationLinkClick';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';



export type HeaderExternalLinkProps = {
	/** Whether the link is active (for styling) */
	isActive?: boolean,
	href: string,
	onClick?: (e: React.MouseEvent) => void,
};

type Props = {
	accountListSection?: React.ReactNode,
	accountNavButton?: React.ReactNode,
	children: React.ReactNode,
	/** Data attributes to add to the expandable li element (e.g. data-secondary-nav-item) */
	dataAttributes?: Record<string, string>,
	icon: React.ReactNode,
	/** When true, chevron icon is hidden (e.g. Content/Performance while sub-items are hidden) */
	hideChevron?: boolean,
	/** When true, trigger shows green (active) when collapsed. Default false; green comes from expansion. */
	isActive?: boolean,
	isExpanded: boolean,
	isSubItemsHidden?: boolean,
	label: React.ReactNode,
	onToggle: () => void,
	phase2CompactLayout?: React.ReactNode,
	/**
	 * When provided, the header uses external link structure (icon + conditional label) with anchor.
	 * Opens in new tab (target="_blank"). Use for Content/Performance links to Conductor app or marketing.
	 * Otherwise, a button trigger is used (Technical, Account, Internal).
	 */
	headerExternalLinkProps?: HeaderExternalLinkProps,
};

/**
 * Expandable navigation section component
 * Used for Technical section that contains sub-items
 */
const ExpandableNavSection: React.FC<Props> = (props) => {
	const {
		accountListSection,
		accountNavButton,
		children,
		dataAttributes,
		icon,
		hideChevron = false,
		isActive = false,
		isExpanded,
		isSubItemsHidden = false,
		label,
		onToggle,
		phase2CompactLayout,
		headerExternalLinkProps,
	} = props;

	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();
	const baseClick = useNavigationLinkClick();

	// Check if we're in Phase 3 (either naturally or through menu expansion)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);

	// Show label when in Phase 3 (either naturally or through menu expansion)
	const shouldShowLabel = effectivePhase3;

	const handleToggle = React.useCallback(
		(e: React.SyntheticEvent) => {
			e.preventDefault();
			onToggle();
		},
		[
			onToggle,
		],
	);

	const iconClasses = classNames({
		'main-nav__expandable-icon': true,
		'main-nav__expandable-icon--expanded': isExpanded,
		'main-nav__expandable-icon--hidden': hideChevron,
	});

	// Generate unique ID for ARIA attributes
	const sectionId = React.useMemo(() => {
		// Use label text to create a stable ID
		const labelText = typeof label === 'string' ? label : 'section';
		return `nav-section-${labelText.toLowerCase().replace(/\s+/g, '-')}`;
	}, [label]);

	// External link trigger (Content, Performance): chevron inside label for consistent alignment
	const externalLinkTriggerContent = headerExternalLinkProps && (
		<div
			aria-expanded={isExpanded}
			className="main-nav__expandable-trigger main-nav__expandable-trigger--with-link"
			role="group"
		>
			<a
				className={classNames({
					'active': headerExternalLinkProps.isActive,
				})}
				href={headerExternalLinkProps.href}
				onClick={(e) => {
					baseClick();
					headerExternalLinkProps.onClick?.(e);
				}}
				rel="noopener noreferrer"
				target="_blank"
			>
				<span
					className={classNames(
						'menu-item-layout',
						{
							'menu-item-layout--is-active': headerExternalLinkProps.isActive,
						},
					)}
				>
					<span className="menu-item-layout__icon">
						{icon}
					</span>
					{shouldShowLabel && (
						<span className="menu-item-layout__label">
							{label}
							{/* Chevron inside label - same as Content/Performance for consistent alignment */}
							<span
								aria-controls={sectionId}
								aria-expanded={isExpanded}
								aria-label={`Toggle ${typeof label === 'string' ? label : 'navigation'} section`}
								className={iconClasses}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleToggle(e);
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										handleToggle(e);
									}
								}}
								role="button"
								tabIndex={0}
							>
								<DropdownIcon
									isActive={isExpanded}
									size={10}
									style={DropdownIconStyle.White}
								/>
							</span>
						</span>
					)}
				</span>
			</a>
		</div>
	);

	const triggerContent = headerExternalLinkProps ? externalLinkTriggerContent : (
		<button
			aria-controls={sectionId}
			aria-expanded={isExpanded}
			aria-label={`Toggle ${typeof label === 'string' ? label : 'navigation'} section`}
			className="main-nav__expandable-trigger"
			onClick={handleToggle}
			type="button"
		>
			{/* Render structure manually - similar to Website component pattern */}
			<span
				className={classNames(
					'menu-item-layout',
					{
						'menu-item-layout--is-active': isActive,
					},
				)}
			>
				<span className="menu-item-layout__icon">
					{icon}
				</span>
				{shouldShowLabel && (
					<span className="menu-item-layout__label">
						{label}
						{/* Render DropdownIcon inside label in Phase 3, just like Website component's additionalIcon */}
						<span className={iconClasses}>
							<DropdownIcon
								isActive={isExpanded}
								size={10}
								style={DropdownIconStyle.White}
							/>
						</span>
					</span>
				)}
			</span>
		</button>
	);

	return (
		<>
			<li
				className="main-nav__item main-nav__item--expandable"
				{...(dataAttributes as React.HTMLAttributes<HTMLLIElement>)}
			>
				{triggerContent}
				{/* Account Navigation Button - rendered right below Technical header, outside sub-items container */}
				{accountNavButton && isExpanded && (
					<div className="main-nav__account-nav-button-wrapper">
						{accountNavButton}
					</div>
				)}
				{/* Account List Section - rendered below AccountNavButton when accounts are shown */}
				{accountListSection && isExpanded && accountListSection}
				{/* Sub-items - shown in Phase 3 or when compact layout is not active */}
				{/* Hide Sub-items when: collapsed, hidden by AccountNavButton, OR when compact layout is shown */}
				<ul
					aria-hidden={!isExpanded || isSubItemsHidden || !!phase2CompactLayout}
					className={classNames(
						'main-nav__sub-items',
						{
							'main-nav__sub-items--hidden': isSubItemsHidden || !!phase2CompactLayout,
						},
					)}
					id={sectionId}
				>
					{children}
				</ul>
			</li>
			{/* Phase 2 Compact Layout - shown instead of Sub-items when in Phase 2 */}
			{/* Only show when: expanded, compact layout provided, and Sub-items are NOT hidden (AccountListSection not visible) */}
			{/* Rendered as siblings of the expandable item, not children (to maintain valid HTML structure) */}
			{phase2CompactLayout && isExpanded && !isSubItemsHidden && phase2CompactLayout}
		</>
	);
};



export default ExpandableNavSection;
